import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../hooks/useThemeMode';

const Section = ({ title, children, isDark }) => (
  <Card variant="outlined" sx={{ mb: 3, border: '1px solid', borderColor: isDark ? '#262626' : '#e5e5e5', bgcolor: isDark ? '#0a0a0a' : '#fff' }}>
    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{title}</Typography>
      {children}
    </CardContent>
  </Card>
);

const StepItem = ({ number, title, description, isDark }) => (
  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
    <Chip label={number} size="small" sx={{ minWidth: 28, height: 28, fontWeight: 700, bgcolor: 'rgba(34,197,94,0.15)', color: '#22c55e' }} />
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: isDark ? '#a3a3a3' : '#525252', mt: 0.25 }}>{description}</Typography>
    </Box>
  </Box>
);

const Guide = () => {
  const navigate = useNavigate();
  const { isDark } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const subtleText = isDark ? '#a3a3a3' : '#525252';

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', mt: { xs: 1, md: 3 }, px: { xs: 1, md: 0 } }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>How It Works</Typography>
        <Typography variant="body1" sx={{ color: subtleText }}>
          Two modes for different privacy needs
        </Typography>
      </Box>

      {/* Overview */}
      <Section title="What is InterNull?" isDark={isDark}>
        <Typography variant="body2" sx={{ color: subtleText, mb: 2 }}>
          InterNull lets you deposit funds on one blockchain and withdraw them anonymously on any supported chain.
          Your deposit is locked in a treasury contract, and you receive cryptographic keys that prove ownership
          without revealing which deposit is yours.
        </Typography>
        <Typography variant="body2" sx={{ color: subtleText }}>
          Keys are generated using Distributed Key Generation (DKG) — no single server ever sees your full private key.
          Three out of five independent nodes each provide a key share, and your browser reconstructs the full key locally.
        </Typography>
      </Section>

      {/* Easy Mode */}
      <Section title="Quick Privacy (Easy Mode)" isDark={isDark}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Chip label="Recommended for most users" size="small" sx={{ bgcolor: 'rgba(34,197,94,0.15)', color: '#22c55e', fontWeight: 600 }} />
        </Box>
        <Typography variant="body2" sx={{ color: subtleText, mb: 2 }}>
          One-step flow: deposit and withdraw in a single session. Your funds are automatically
          relay-withdrawn to the addresses you specify — no gas needed on the destination chain.
        </Typography>

        <StepItem number="1" title="Select token and configure keys" isDark={isDark}
          description="Choose a token (ETH, USDC, etc.), then add one or more keys — each with a target chain, amount, and recipient address." />
        <StepItem number="2" title="Deposit" isDark={isDark}
          description="Lock your funds in the treasury contract on any supported chain. Your wallet signs one transaction." />
        <StepItem number="3" title="Keys auto-generated" isDark={isDark}
          description="After 2 block confirmations, the app automatically requests key shares from DKG nodes and reconstructs your private keys in the browser." />
        <StepItem number="4" title="Auto-withdrawal via relay" isDark={isDark}
          description="Each key is immediately relay-withdrawn to the recipient address you specified. The relayer submits the on-chain transaction — your address never appears." />

        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" sx={{ color: '#f59e0b', fontWeight: 500 }}>
          Privacy trade-off: withdrawals happen shortly after deposit, which makes timing analysis easier.
          Splitting into multiple keys across different chains helps, but for maximum anonymity use Pro Mode.
        </Typography>

        <Button variant="outlined" endIcon={<ArrowForward />} onClick={() => navigate('/easy')} sx={{ mt: 2 }}>
          Open Quick Privacy
        </Button>
      </Section>

      {/* Pro Mode */}
      <Section title="Pro Mode" isDark={isDark}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Chip label="Maximum privacy" size="small" sx={{ bgcolor: 'rgba(59,130,246,0.15)', color: '#3b82f6', fontWeight: 600 }} />
        </Box>
        <Typography variant="body2" sx={{ color: subtleText, mb: 2 }}>
          Full control over when and how you withdraw. Deposit now, withdraw days or weeks later —
          from a different wallet, device, or network. Both options support partial withdrawals, so you
          can withdraw one key today and the rest over time. Two options for every deposit:
        </Typography>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, mt: 2, color: '#22c55e' }}>
          Option 1: Unlock (one-click)
        </Typography>
        <StepItem number="1" title="Click 'Unlock' on any deposit" isDark={isDark}
          description="Choose chain, token, amount, and recipient address — all in one dialog." />
        <StepItem number="2" title="One-click execution" isDark={isDark}
          description="Sign → request keys → relay withdrawal. You don't have to withdraw everything at once — withdraw one key now and come back for the rest later." />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, mt: 3, color: '#3b82f6' }}>
          Option 2: Save Keys (delayed withdrawal)
        </Typography>
        <StepItem number="1" title="Click 'Save Keys' on a deposit" isDark={isDark}
          description="Configure what keys you want (chain, token, amount). No recipient address needed yet." />
        <StepItem number="2" title="Download encrypted key backup" isDark={isDark}
          description="Keys are encrypted with your password and downloaded as a file. Move it to another device if needed." />
        <StepItem number="3" title="Withdraw anytime later" isDark={isDark}
          description="Upload the key file on any device, enter a recipient address, and withdraw — days, weeks, or months after deposit." />

        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" sx={{ color: subtleText }}>
          <strong>Why this is more private:</strong> by separating deposit and withdrawal in time, you break the
          timing link between them. Combined with cross-chain withdrawal and different wallet addresses,
          this makes it extremely difficult to correlate deposits with withdrawals.
        </Typography>

        <Typography variant="body2" sx={{ color: subtleText, mt: 1 }}>
          <strong>Partial withdrawals:</strong> you don't have to withdraw the full deposit at once. For example,
          deposit 5 USDC today, withdraw 2 USDC next week and 3 USDC next month — each to a different chain and address.
        </Typography>

        <Button variant="outlined" endIcon={<ArrowForward />} onClick={() => navigate('/dashboard')} sx={{ mt: 2 }}>
          Open Pro Mode
        </Button>
      </Section>

      {/* Supported Chains */}
      <Section title="Supported Chains" isDark={isDark}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {['Ethereum Sepolia', 'Base Sepolia', 'Polygon Amoy', 'BNB Testnet', 'Hyperliquid Testnet', 'Solana Devnet'].map(chain => (
            <Chip key={chain} label={chain} variant="outlined" size="small" />
          ))}
        </Box>
        <Typography variant="body2" sx={{ color: subtleText, mt: 2 }}>
          Deposit on any chain, withdraw on any other chain. Cross-chain transfers are enabled automatically
          when token mappings exist between chains.
        </Typography>
      </Section>

      {/* Security */}
      <Section title="Security Model" isDark={isDark}>
        <StepItem number="1" title="Distributed Key Generation (3-of-5)" isDark={isDark}
          description="No single node ever holds your full private key. Three independent nodes must cooperate to produce key shares." />
        <StepItem number="2" title="Client-side reconstruction" isDark={isDark}
          description="Key shares are combined in your browser using Lagrange interpolation. The full key never exists on any server." />
        <StepItem number="3" title="On-chain nullifiers" isDark={isDark}
          description="Each key can only be used once. The smart contract tracks nullifiers to prevent double-spending." />
        <StepItem number="4" title="Relay privacy" isDark={isDark}
          description="Gas-free relay withdrawals mean the relayer's address appears on-chain, not yours." />
      </Section>

      {/* FAQ */}
      <Section title="FAQ" isDark={isDark}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>What happens if I lose my keys?</Typography>
          <Typography variant="body2" sx={{ color: subtleText }}>
            If you haven't withdrawn yet, you can request new keys from the DKG nodes using the same deposit transaction.
            The system is idempotent — the same deposit always produces the same keys.
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Can I partially withdraw a deposit?</Typography>
          <Typography variant="body2" sx={{ color: subtleText }}>
            Yes. For example, deposit 5 USDC and claim a 2 USDC key now. The remaining 3 USDC stays available for future claims.
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>What tokens are supported?</Typography>
          <Typography variant="body2" sx={{ color: subtleText }}>
            ETH, USDC, SOL, HYPE, tBNB, POL, and LINK. Each has specific denominations (e.g., 0.001 ETH, 1 USDC, 0.1 SOL).
            The exact list depends on what's registered on each chain's treasury contract.
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Is relay withdrawal free?</Typography>
          <Typography variant="body2" sx={{ color: subtleText }}>
            The relayer covers gas costs. For ERC-20 tokens, a small fee (up to 5% of the withdrawal amount) may be deducted
            to compensate the relayer. For native tokens (ETH, SOL), gas reimbursement is deducted from the withdrawal.
          </Typography>
        </Box>
      </Section>
    </Box>
  );
};

export default Guide;
